import { useState, forwardRef, useImperativeHandle } from "react";
import { Sparkles, Loader2, Lightbulb } from "lucide-react";
import {
    adminCreateContentPackage,
    adminContentSuggestTopics,
} from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
    "Water Intrusion",
    "Roofing",
    "Foundation & Structure",
    "Electrical",
    "Plumbing",
    "HVAC",
    "Exterior & Drainage",
    "Insulation & Energy",
    "Pests & Wood-Destroying Organisms",
    "Negotiation Strategy",
    "Closing Process",
    "Inspection Wording",
];

const INTENTS = [
    "educational",
    "educational + negotiation planning",
    "decision-support",
    "comparison shopping",
];

const TOOLS = [
    "Repair Exposure Calculator",
    "Closing Cost Calculator",
    "Water Intrusion Risk Checker",
    "Roof Concern Assessor",
    "Foundation Concern Checker",
    "Electrical Panel Risk Tool",
    "Negotiation Letter Builder",
];

function GeneratePackageFormInner({ onCreated }, ref) {
    const [form, setForm] = useState({
        primary_keyword: "",
        title: "",
        category: CATEGORIES[0],
        audience: "homebuyers under contract",
        search_intent: INTENTS[1],
        related_tool: "",
        product_cta: "Upload Inspection",
        location_focus: "",
        notes: "",
        skip_mini_tool: false,
        skip_infographic: false,
    });
    const [loading, setLoading] = useState(false);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const suggestTopics = async () => {
        setSuggestLoading(true);
        try {
            const r = await adminContentSuggestTopics();
            setSuggestions(r.topics || []);
            toast.success(
                `${r.topics?.length || 0} topics suggested · ${r.signal_size?.feature_requests || 0} feature requests considered`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Topic suggestion failed",
            );
        } finally {
            setSuggestLoading(false);
        }
    };

    const applyTopic = (topic) => {
        setForm((p) => ({
            ...p,
            primary_keyword: topic.primary_keyword || p.primary_keyword,
            title: topic.title_idea || "",
            category: CATEGORIES.includes(topic.category) ? topic.category : p.category,
            audience: topic.audience || p.audience,
            search_intent: INTENTS.includes(topic.search_intent)
                ? topic.search_intent
                : p.search_intent,
        }));
        setSuggestions([]);
        toast.success("Topic applied to form");
        // Scroll the keyword input into view so the admin sees the change.
        setTimeout(() => {
            try {
                document
                    .querySelector('[data-testid="ce-input-primary-keyword"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
            } catch {
                /* no-op */
            }
        }, 60);
    };

    // Expose applyTopic to parent components (OpportunityCards uses this).
    useImperativeHandle(ref, () => ({ applyTopic }), [applyTopic]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.primary_keyword.trim()) {
            toast.error("Primary keyword is required.");
            return;
        }
        setLoading(true);
        const tStart = Date.now();
        try {
            const pkg = await adminCreateContentPackage(form);
            const seconds = Math.round((Date.now() - tStart) / 1000);
            toast.success(
                `Generated "${pkg.title || pkg.package_id}" in ${seconds}s.`,
            );
            onCreated?.(pkg);
        } catch (err) {
            console.error(err);
            const raw =
                err?.response?.data?.detail ||
                err?.message ||
                "Generation failed.";
            // Detect Emergent Universal Key budget-exceeded and surface a
            // friendly, actionable message instead of the raw stack trace.
            const lower = String(raw).toLowerCase();
            if (lower.includes("budget has been exceeded") || lower.includes("budget exceeded")) {
                toast.error("Universal Key budget exceeded", {
                    description:
                        "Your Emergent LLM key is out of credits. Go to Profile → Universal Key → Add Balance (or enable Auto top-up) and try again.",
                    duration: 10000,
                });
            } else if (lower.includes("rate_limit") || lower.includes("rate limit")) {
                toast.error("LLM provider rate-limited", {
                    description: "Wait 30-60 seconds and try again.",
                });
            } else {
                toast.error("Generation failed", {
                    description: String(raw).slice(0, 240),
                    duration: 8000,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="bl-card p-6 space-y-5"
            data-testid="generate-package-form"
        >
            <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-5 w-5 text-bl-primary" />
                <h2 className="font-display font-semibold text-xl text-bl tracking-tight">
                    Generate New Content Package
                </h2>
            </div>
            <p className="text-sm text-bl-muted">
                Enter a topic + intent. We'll run Claude Sonnet 4.5 for the
                long-form content and GPT-4.1-mini for SEO/structure. One
                generation pass produces 7+ structured assets in ~3 minutes.
            </p>

            <div
                className="rounded border border-dashed border-bl/50 p-3"
                data-testid="ce-topic-suggester"
            >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-sm text-bl">
                        <Lightbulb className="h-4 w-4 text-[var(--bl-amber)]" />
                        Need ideas? Suggest topics from user feature requests.
                    </div>
                    <button
                        type="button"
                        onClick={suggestTopics}
                        disabled={suggestLoading}
                        className="bl-btn-ghost text-xs gap-1.5"
                        data-testid="ce-suggest-topics-btn"
                    >
                        {suggestLoading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Lightbulb className="h-3 w-3" />
                        )}
                        Suggest 5 topics
                    </button>
                </div>
                {suggestions.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="ce-topic-suggestions">
                        {suggestions.map((s, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-3 p-2 rounded bg-bl-bg"
                                data-testid={`ce-topic-suggestion-${i}`}
                            >
                                <div className="flex-1 min-w-0 text-sm">
                                    <div className="font-medium text-bl">{s.title_idea}</div>
                                    <div className="text-xs text-bl-muted">
                                        <code>{s.primary_keyword}</code> · {s.category} · {s.search_intent}
                                    </div>
                                    <div className="text-xs text-bl-muted italic mt-0.5">
                                        {s.rationale}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => applyTopic(s)}
                                    className="bl-btn-primary text-xs px-2 py-1"
                                    data-testid={`ce-topic-apply-${i}`}
                                >
                                    Use
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="text-sm text-bl">
                    <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                        Primary keyword *
                    </span>
                    <input
                        type="text"
                        required
                        value={form.primary_keyword}
                        onChange={(e) => set("primary_keyword", e.target.value)}
                        placeholder="e.g., sump pump home inspection"
                        className="w-full bl-input"
                        data-testid="ce-input-primary-keyword"
                    />
                </label>
                <label className="text-sm text-bl">
                    <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                        Title hint (optional)
                    </span>
                    <input
                        type="text"
                        value={form.title}
                        onChange={(e) => set("title", e.target.value)}
                        placeholder="leave blank to auto-generate"
                        className="w-full bl-input"
                        data-testid="ce-input-title"
                    />
                </label>
                <label className="text-sm text-bl">
                    <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                        Category *
                    </span>
                    <select
                        value={form.category}
                        onChange={(e) => set("category", e.target.value)}
                        className="w-full bl-input"
                        data-testid="ce-input-category"
                    >
                        {CATEGORIES.map((c) => (
                            <option key={c}>{c}</option>
                        ))}
                    </select>
                </label>
                <label className="text-sm text-bl">
                    <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                        Audience
                    </span>
                    <input
                        type="text"
                        value={form.audience}
                        onChange={(e) => set("audience", e.target.value)}
                        className="w-full bl-input"
                        data-testid="ce-input-audience"
                    />
                </label>
                <label className="text-sm text-bl">
                    <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                        Search intent
                    </span>
                    <select
                        value={form.search_intent}
                        onChange={(e) => set("search_intent", e.target.value)}
                        className="w-full bl-input"
                        data-testid="ce-input-intent"
                    >
                        {INTENTS.map((i) => (
                            <option key={i}>{i}</option>
                        ))}
                    </select>
                </label>
                <label className="text-sm text-bl">
                    <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                        Related tool
                    </span>
                    <select
                        value={form.related_tool}
                        onChange={(e) => set("related_tool", e.target.value)}
                        className="w-full bl-input"
                        data-testid="ce-input-related-tool"
                    >
                        <option value="">(none)</option>
                        {TOOLS.map((t) => (
                            <option key={t}>{t}</option>
                        ))}
                    </select>
                </label>
                <label className="text-sm text-bl">
                    <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                        Product CTA focus
                    </span>
                    <input
                        type="text"
                        value={form.product_cta}
                        onChange={(e) => set("product_cta", e.target.value)}
                        className="w-full bl-input"
                        data-testid="ce-input-product-cta"
                    />
                </label>
                <label className="text-sm text-bl">
                    <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                        Location focus (optional)
                    </span>
                    <input
                        type="text"
                        value={form.location_focus}
                        onChange={(e) => set("location_focus", e.target.value)}
                        placeholder="e.g., Pacific Northwest, Florida coast"
                        className="w-full bl-input"
                        data-testid="ce-input-location"
                    />
                </label>
            </div>

            <label className="text-sm text-bl block">
                <span className="block mb-1 text-xs uppercase tracking-wide text-bl-muted">
                    Notes / instructions
                </span>
                <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Tone notes, specific angles, must-cover items…"
                    className="w-full bl-input"
                    data-testid="ce-input-notes"
                />
            </label>

            <div className="flex items-center gap-6 flex-wrap text-sm">
                <label className="flex items-center gap-2 text-bl">
                    <input
                        type="checkbox"
                        checked={form.skip_infographic}
                        onChange={(e) => set("skip_infographic", e.target.checked)}
                        data-testid="ce-skip-infographic"
                    />
                    Skip infographic
                </label>
                <label className="flex items-center gap-2 text-bl">
                    <input
                        type="checkbox"
                        checked={form.skip_mini_tool}
                        onChange={(e) => set("skip_mini_tool", e.target.checked)}
                        data-testid="ce-skip-mini-tool"
                    />
                    Skip mini-tool
                </label>
            </div>

            <button
                type="submit"
                disabled={loading}
                className="bl-btn-primary gap-2"
                data-testid="ce-generate-btn"
            >
                {loading ? (
                    <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating… this takes 2-4 minutes
                    </>
                ) : (
                    <>
                        <Sparkles className="h-4 w-4" />
                        Generate Content Package
                    </>
                )}
            </button>
        </form>
    );
}

const GeneratePackageForm = forwardRef(GeneratePackageFormInner);
export default GeneratePackageForm;
